import React, { useEffect, useState } from 'react';
import { KTSVG } from '../../../../_metronic/helpers';
import type { LibraryApi, LibraryTemplateFilters } from '../core/libraryApi';
import { BUNKER_PRESET_SECTIONS } from './bunkerReportSchema';
import { ARRIVAL_PRESET_SECTIONS } from './arrivalReportSchema';
import { FUEL_CHANGEOVER_PRESET_SECTIONS, isFuelChangeoverReportName } from './fuelChangeoverReportSchema';
import SearchableSelect from '../../../shared/components/SearchableSelect';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onReportAdded: (tempId: number) => void;
    api: LibraryApi;
    templateContext?: LibraryTemplateFilters;
    preset?: 'bunker' | 'arrival' | 'fuelChangeover';
}

export type { Props as AddReportModalProps };

const AddReportModal: React.FC<Props> = ({ onClose, isOpen, onReportAdded, api, templateContext, preset }) => {
    const [reportName, setReportName] = useState("");
    const [error, setError] = useState("");
    const [formType, setFormType] = useState(templateContext?.formType ?? "FORM");

    useEffect(() => {
        if (!isOpen) return;
        setError("");
        setReportName(
            preset === 'bunker'
                ? 'Bunker Report'
                : preset === 'arrival'
                    ? 'Arrival Report'
                    : preset === 'fuelChangeover'
                        ? 'Fuel Changeover Report'
                        : ""
        );
        setFormType(templateContext?.formType ?? "FORM");
    }, [isOpen, preset, templateContext?.formType]);

    const extractId = (value: any) => {
        const n = Number(value?.id ?? value?.menuId ?? value?.submenuId ?? value?.fieldId);
        return Number.isFinite(n) ? n : null;
    };

    const createBunkerPreset = async (menuId: number) => {
        for (let sectionIndex = 0; sectionIndex < BUNKER_PRESET_SECTIONS.length; sectionIndex += 1) {
            const section = BUNKER_PRESET_SECTIONS[sectionIndex];
            const submenu = await api.createSubmenuItem(menuId, section.name, true, sectionIndex + 1);
            const submenuId = extractId(submenu);

            if (!submenuId) {
                throw new Error(`Failed to create bunker section "${section.name}"`);
            }

            for (const field of section.fields) {
                await api.createField(
                    submenuId,
                    field.label,
                    field.fieldType,
                    field.readOnly,
                    field.required,
                    field.active ?? true,
                    undefined,
                    field.unit,
                    undefined,
                    undefined,
                    undefined
                );
            }
        }
    };

    const createArrivalPreset = async (menuId: number) => {
        for (let sectionIndex = 0; sectionIndex < ARRIVAL_PRESET_SECTIONS.length; sectionIndex += 1) {
            const section = ARRIVAL_PRESET_SECTIONS[sectionIndex];
            const submenu = await api.createSubmenuItem(menuId, section.name, true, sectionIndex + 1);
            const submenuId = extractId(submenu);

            if (!submenuId) {
                throw new Error(`Failed to create arrival section "${section.name}"`);
            }

            for (const field of section.fields) {
                await api.createField(
                    submenuId,
                    field.label,
                    field.fieldType,
                    field.readOnly,
                    field.required,
                    field.active ?? true,
                    undefined,
                    field.unit,
                    undefined,
                    undefined,
                    field.options ?? null
                );
            }
        }
    };

    const createFuelChangeoverPreset = async (menuId: number) => {
        for (let sectionIndex = 0; sectionIndex < FUEL_CHANGEOVER_PRESET_SECTIONS.length; sectionIndex += 1) {
            const section = FUEL_CHANGEOVER_PRESET_SECTIONS[sectionIndex];
            const submenu = await api.createSubmenuItem(menuId, section.name, true, sectionIndex + 1);
            const submenuId = extractId(submenu);

            if (!submenuId) {
                throw new Error(`Failed to create fuel changeover section "${section.name}"`);
            }

            for (const field of section.fields) {
                await api.createField(
                    submenuId,
                    field.label,
                    field.fieldType,
                    field.readOnly,
                    field.required,
                    field.active ?? true,
                    undefined,
                    field.unit,
                    undefined,
                    undefined,
                    field.options ?? null
                );
            }
        }
    };

    const handleAddMenuItem = async () => {
        if (!reportName.trim()) {
            setError("Please enter a report name");
            toast.warning("Please enter a report name");
            return;
        }

        try {
            if (api.id === 'qhse' && !templateContext?.companyGroupAdminId) {
                setError("Please select a company before creating a template");
                toast.warning("Please select a company before creating a template");
                return;
            }
            const response = await api.createTemplate(reportName, {
                ...templateContext,
                formType,
            });
            console.log("template creation response", response);

            const tempId = response.id;
            const menuResponse = await api.createMenuItem(tempId, reportName, true);
            const menuId = extractId(menuResponse);
            const shouldUseBunkerPreset =
                preset === 'bunker' || /\bbunker\b/i.test(reportName.trim());
            const shouldUseArrivalPreset =
                preset === 'arrival' || /\barrival\b/i.test(reportName.trim());
            const shouldUseFuelChangeoverPreset =
                preset === 'fuelChangeover' || isFuelChangeoverReportName(reportName.trim());

            if (shouldUseBunkerPreset) {
                if (!menuId) {
                    throw new Error('Failed to create bunker report menu');
                }
                await createBunkerPreset(menuId);
            }

            if (shouldUseArrivalPreset) {
                if (!menuId) {
                    throw new Error('Failed to create arrival report menu');
                }
                await createArrivalPreset(menuId);
            }

            if (shouldUseFuelChangeoverPreset) {
                if (!menuId) {
                    throw new Error('Failed to create fuel changeover report menu');
                }
                await createFuelChangeoverPreset(menuId);
            }

            toast.success("Report created successfully");
            onReportAdded(tempId);
            onClose();
        } catch (err) {
            console.error('Failed to add report:', err);
            const msg = getApiErrorMessage(err, 'Failed to create report');
            setError(msg);
            toast.error(msg);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="custom-modal-header d-flex justify-content-between align-items-center">
                    <h5 className="m-0">
                        {preset === 'bunker'
                            ? 'Add Bunker Report'
                            : preset === 'arrival'
                                ? 'Add Arrival Report'
                                : preset === 'fuelChangeover'
                                    ? 'Add Fuel Changeover Report'
                                    : 'Add Report'}
                    </h5>
                    <button className="close-btn" onClick={onClose}>
                        <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
                    </button>
                </div>

                <div className="custom-modal-body">
                    {error && <div className='text-danger mb-3'>{error}</div>}
                    <label className="modal_label">Report Name<span className="text-danger">*</span></label>
                    <input
                        type="text"
                        value={reportName}
                        onChange={(e) => setReportName(e.target.value)}
                        className="form-control"
                        placeholder={
                            preset === 'bunker'
                                ? 'Bunker Report'
                                : preset === 'arrival'
                                    ? 'Arrival Report'
                                    : preset === 'fuelChangeover'
                                        ? 'Fuel Changeover Report'
                                        : ''
                        }
                        readOnly={preset === 'bunker' || preset === 'arrival' || preset === 'fuelChangeover'}
                    />
                    {api.features.showFormTypeSelect && (
                        <>
                            <label className="modal_label mt-4">Type</label>
                            <SearchableSelect
                                className="form-select"
                                value={formType}
                                onChange={(e) => setFormType(e.target.value)}
                            >
                                <option value="FORM">Form</option>
                                <option value="CHECKLIST">Checklist</option>
                            </SearchableSelect>
                        </>
                    )}
                </div>

                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn_secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn_success" onClick={handleAddMenuItem}>Add</button>
                </div>
            </div>
        </div>
    );
};

export default AddReportModal;
